import { ApiError } from "@/lib/api/api-error";
import { AgentGatewayService } from "@/services/agent-gateway-service";
import type {
  AgentSession,
  ApprovalDecisionPayload,
} from "@/services/types/agent-gateway";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { reconcileApprovalDecisionEvents } from "./message-utils";
import { agentGatewayQueryKeys } from "./query-options";

type ApprovalMutationVariables = {
  payload: ApprovalDecisionPayload;
  idempotencyKey: string;
};

function getApprovalError(error: unknown) {
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return "You no longer have permission to decide this approval.";
    }
    if (error.status === 404) {
      return "This approval request no longer exists.";
    }
    if (error.status === 409) {
      return "This approval changed or was already decided. Refresh the session.";
    }
    if (error.status === 400) {
      return "Review the approval decision and try again.";
    }
  }
  return "The approval decision could not be recorded. Please try again.";
}

export function useAgentApprovalController(
  agentKey: string,
  sessionId: string,
) {
  const queryClient = useQueryClient();
  const scopeKey = `${agentKey}:${sessionId}`;
  const inFlightApprovalRef = useRef<string | null>(null);
  const inFlightContinuationRef = useRef<string | null>(null);
  const [pendingApprovalId, setPendingApprovalId] = useState<string | null>(
    null,
  );
  const [pendingContinuationId, setPendingContinuationId] = useState<
    string | null
  >(null);
  const [errorByApprovalId, setErrorByApprovalId] = useState<
    Record<string, string>
  >({});

  const mutation = useMutation({
    mutationFn: ({ payload, idempotencyKey }: ApprovalMutationVariables) =>
      AgentGatewayService.decideApproval(
        agentKey,
        sessionId,
        payload,
        idempotencyKey,
      ),
    onSuccess: async (response) => {
      inFlightApprovalRef.current = null;
      const sessionKey = agentGatewayQueryKeys.session(agentKey, sessionId);
      await queryClient.cancelQueries({ exact: true, queryKey: sessionKey });
      queryClient.setQueryData<AgentSession>(sessionKey, (current) =>
        current
          ? {
              ...current,
              events: reconcileApprovalDecisionEvents(
                current.events,
                response.events,
              ),
            }
          : current,
      );
      setErrorByApprovalId((current) => {
        const next = { ...current };
        delete next[response.approval_id];
        return next;
      });
      setPendingApprovalId(null);
      void queryClient.invalidateQueries({ exact: true, queryKey: sessionKey });
      void queryClient.invalidateQueries({
        exact: true,
        queryKey: agentGatewayQueryKeys.sessions(agentKey),
      });
    },
    onError: (error, variables) => {
      inFlightApprovalRef.current = null;
      setPendingApprovalId(null);
      setErrorByApprovalId((current) => ({
        ...current,
        [variables.payload.approval_id]: getApprovalError(error),
      }));
      if (error instanceof ApiError && [401, 403, 404].includes(error.status)) {
        void queryClient.invalidateQueries({
          queryKey: agentGatewayQueryKeys.availableAgents(),
        });
      }
    },
  });

  const continuationMutation = useMutation({
    mutationFn: ({
      approvalId,
      idempotencyKey,
    }: {
      approvalId: string;
      idempotencyKey: string;
    }) =>
      AgentGatewayService.retryApprovalContinuation(
        agentKey,
        sessionId,
        approvalId,
        idempotencyKey,
      ),
    onSuccess: (response) => {
      inFlightContinuationRef.current = null;
      const sessionKey = agentGatewayQueryKeys.session(agentKey, sessionId);
      queryClient.setQueryData<AgentSession>(sessionKey, (current) =>
        current
          ? {
              ...current,
              events: reconcileApprovalDecisionEvents(
                current.events,
                response.events,
              ),
            }
          : current,
      );
      setPendingContinuationId(null);
      void queryClient.invalidateQueries({ exact: true, queryKey: sessionKey });
    },
    onError: () => {
      inFlightContinuationRef.current = null;
      setPendingContinuationId(null);
    },
  });

  useEffect(() => {
    inFlightApprovalRef.current = null;
    inFlightContinuationRef.current = null;
    setPendingApprovalId(null);
    setPendingContinuationId(null);
    setErrorByApprovalId({});
  }, [scopeKey]);

  const decideApproval = (payload: ApprovalDecisionPayload) => {
    if (inFlightApprovalRef.current) {
      return;
    }
    inFlightApprovalRef.current = payload.approval_id;
    setPendingApprovalId(payload.approval_id);
    setErrorByApprovalId((current) => {
      const next = { ...current };
      delete next[payload.approval_id];
      return next;
    });
    mutation.mutate({
      payload,
      idempotencyKey: crypto.randomUUID(),
    });
  };

  const retryApprovalContinuation = (approvalId: string) => {
    if (inFlightContinuationRef.current !== null) {
      return;
    }
    inFlightContinuationRef.current = approvalId;
    setPendingContinuationId(approvalId);
    continuationMutation.mutate({
      approvalId,
      idempotencyKey: crypto.randomUUID(),
    });
  };

  return {
    decideApproval,
    errorByApprovalId,
    pendingApprovalId,
    pendingContinuationId,
    retryApprovalContinuation,
  };
}
